/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/CombinePath.ts
 * @create:      2025-07-01 18:00:38.830
 * @modify:      2025-07-11 21:19:12.956
 * @version:     6.0.0
 * @times:       7
 * @lines:       71
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { Strings_BackSlash, Strings_Slash } from "../const/Strings";
import { ReplaceAll } from "../string/ReplaceAll";
import { TrimAll } from "../string/TrimAll";
import { TrimPath } from "./TrimPath";

/**
 * Combines a base path with one or more additional path segments into a single normalized path string.
 * 
 * This function trims whitespace from the base path and each additional segment, replaces all backslashes
 * with forward slashes, and ensures that there are no duplicate or trailing slashes between segments.
 * Empty segments are skipped.
 * 
 * @param base - The base path to start from.
 * @param paths - Additional path segments to combine with the base path.
 * @returns The combined and normalized path string.
 * 
 * @since 1.1.7
 * @version 2025-07-02
 */
export function CombinePath(base: string, ...paths: string[]) {
    let result = TrimPath(base.trim());
    result = ReplaceAll(result, Strings_BackSlash, Strings_Slash);

    result = paths
        .reduce((accumulator, currentValue) => {
            const part = TrimAll(currentValue.trim(), [Strings_Slash, Strings_BackSlash]);

            if (part === "") {
                return accumulator; // Skip empty segments
            }

            return accumulator + Strings_Slash + part;
        }, result);

    result = TrimPath(result);

    return result;
}
